import React, {useEffect, useMemo, useRef, useState} from 'react'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {useNavigate} from 'react-router-dom'
import {ModeleMesActions, ModeleValueLabel} from '../../../../components/Models'
import {TextInput} from '../../../../components/inputs/TextInput'
import {SelectInput} from '../../../../components/inputs/SelectInput'
import {origineMesActions} from '../../../../components/Data'
import {DateInput} from '../../../../components/inputs/DateInput'
import {
  finishActions,
  getStatutActions,
  getTypeActions,
  getUtilisateursWithoutPagination,
} from '../../../../components/requests'
import {useAuth} from '../../../modules/auth'
import SearchClient from '../../../../components/SearchClient'
import moment, {Moment} from 'moment'
import SearchDemande from '../../../../components/SearchDemande'
import {useLocation} from 'react-router'
import Swal from 'sweetalert2'
import {toast} from 'react-toastify'
import {shouldDisplayFonctionnalite} from '../../../../components/helpers'

type LoadingSelectState = {
  [key: string]: boolean
}

const mesActionsSchema = Yup.object().shape({
  titre: Yup.string()
    .required('Le titre est requis')
    .min(3, 'Le titre doit contenir au moins 3 caractères'),
  type: Yup.string().required('Le type est requis'),
  statut: Yup.string().required('Le statut est requis'),
  origine: Yup.string().required("L'origine est requis"),
  dateEcheance: Yup.string().required('La date échéance est requise'),
  userAssign: Yup.string().required("L'utilisateur assigné est requis"),
})

const MesActionsForm = (props) => {
  const navigate = useNavigate()
  const location = useLocation()
  const {currentUser} = useAuth()

  const WORK_START = 8
  const WORK_END = 18
  const DEFAULT_DURATION = 4

  const isCreate = props?.isCreate ?? ((props?.readOnly || props?.viaDemande) && !props?.isRealisation)

  /**
   * Valeurs initiales
   */
  const initialValues: ModeleMesActions = useMemo(
    () => ({
      ...props?.data,
      demandeId: props?.data?.demande?.id ?? '',
      clientId: props?.data?.client?.id ?? '',
      commentaire: props?.data?.commentaire ?? '',
      commentaireResolution: props?.data?.commentaireResolution ?? '',
      type: props?.data?.type?.id ?? '',
      statut: props?.data?.statut?.id ?? '',
      titre: props?.data?.titre ||
        (props.viaDemande
          ? props?.data?.commentaire?.split(/\s+/).slice(0, 3).join(' ')
          : ''),
      dateRealisation: props?.data?.dateRealisation ?? '',
      dateEcheance: props?.data?.dateEcheance ?? '',
      userAssign: props?.data?.assigneeA?.id?.toString() ?? currentUser?.id?.toString() ?? '',
    }),
    [props?.data, props?.viaDemande, currentUser?.id]
  )

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<ModeleMesActions>(initialValues)
  const [datePickerBlocked, setDatePickerBlocked] = useState(false)
  const formContainerRef = useRef<HTMLDivElement | null>(null)
  const [dateEcheance, setDateEcheance] = useState<string | null>(props?.data?.dateEcheance ?? null)
  const [dateRealisation, setDateRealisation] = useState<string | null>(props?.data?.dateRealisation ?? null)
  const [typeActions, setTypeActions] = useState<Array<ModeleValueLabel>>([])
  const [statutActions, setStatutActions] = useState<Array<ModeleValueLabel>>([])
  const [utilisateurs, setUtilisateurs] = useState<Array<ModeleValueLabel>>([])
  const [demandeSelected, setDemandeSelected] = useState<any>(null)
  const [loadingSelects, setLoadingSelects] = useState<LoadingSelectState>({
    statut: false,
    type: false,
    userAssign: false,
  })
  const selectsReady = loadingSelects.type && loadingSelects.statut && loadingSelects.userAssign
  const emptyValues: ModeleMesActions = {
    titre: '',
    origine: 'Manuelle',
    dateEcheance: '',
    commentaire: '',
    commentaireResolution: '',
    prioritaire: false,
    statut: statutActions?.[0]?.value?.toString(),
    type: typeActions?.[0]?.value?.toString(),
    dateRealisation: '',
  }

  /**
   * Formik
   */
  const formik = useFormik<ModeleMesActions>({
    initialValues,
    enableReinitialize: true,
    validateOnMount: false,
    validationSchema: mesActionsSchema,
    onSubmit: (values) => {
      const statutLabel = statutActions.find(
        (s) =>
          Number(s.value) ===
          Number(values.statut)
      )?.label

      const updatedData = Object.assign({...initialValues}, values)

      if (props?.isRealisation && statutLabel === 'Réalisée') {
        confirmRealisation(updatedData)
        return
      }
      setLoading(true)

      props.submitForm(updatedData, () => {
        formik.resetForm({
          values: emptyValues,
        })
        setDateEcheance(null)
        setDateRealisation(null)
        setLoading(false)
      })
    },
  })

  const setSelectLoading = (key: string, isLoading: boolean) => {
    setLoadingSelects((prev) => ({
      ...prev,
      [key]: isLoading,
    }))
  }

  const getTypeActionsData = () => {
    getTypeActions()
      .then((typeActionsRequest) => {
        if (typeActionsRequest?.data !== undefined) {
          const transformedData = typeActionsRequest.data
            .filter(
              (ta) => !(location.pathname.includes('ajouter') && ta.libelle === 'Message Client')
            )
            .map((item) => ({
              value: item.id,
              label: item.libelle,
            }))
          setTypeActions([...transformedData])
          const selectedType = props?.data?.type?.id ?? transformedData[0]?.value ?? ''

          formik.setFieldValue('type', selectedType.toString(), false)

          formik.setFieldTouched('type', false, false)
          formik.setFieldError('type', undefined)

          setSelectLoading('type', true)

          setData((prev) => ({
            ...prev,
            type: selectedType.toString(),
          }))

          setSelectLoading('type', true)
        }
      })
      .catch((e) => {
        setLoading(false)
      })
  }

  const getStatutActionsData = () => {
    getStatutActions()
      .then((statutActionsRequest) => {
        if (statutActionsRequest?.data !== undefined) {
          const transformedData = statutActionsRequest.data.map((item) => ({
            value: item.id,
            label: item.libelle,
          }))
          setStatutActions([...transformedData])
          const selectedStatut = props?.data?.statut?.id ?? transformedData[0]?.value ?? ''

          formik.setFieldValue('statut', selectedStatut.toString(), false)

          formik.setFieldTouched('statut', false, false)
          formik.setFieldError('statut', undefined)

          setSelectLoading('statut', true)

          setData((prev) => ({
            ...prev,
            statut: selectedStatut.toString(),
          }))

          setSelectLoading('statut', true)
        }
      })
      .catch((e) => {
        setLoading(false)
      })
  }

  const getUtilisateursData = () => {
    getUtilisateursWithoutPagination()
      .then((usersRequest) => {
        if (usersRequest?.data !== undefined) {
          const transformedData = usersRequest.data.map((item) => ({
            value: item.id,
            label: item.id === currentUser?.id ? `${item.nom} (Moi)` : item.nom,
          }))
          setUtilisateurs([...transformedData])

          let userAssignId = props?.data?.assigneeA?.id ?? currentUser?.id
          const currentUserOption = transformedData.find((user) => user.value === userAssignId)
          const selectedUserValue = currentUserOption
            ? currentUserOption.value
            : transformedData[0]?.value

          formik.setFieldValue('userAssign', selectedUserValue, false)
          setSelectLoading('userAssign', true)
        }
      })
      .catch((e) => {
        setLoading(false)
      })
  }

  useEffect(() => {
    getTypeActionsData()
    getStatutActionsData()
    getUtilisateursData()
  }, [])

  /**
   * Calcul de la date d'échéance
   *
   * Règle d'arrondi :
   *
   * 08:00 -> 08:00
   * 08:15 -> 08:00
   * 08:30 -> 08:00
   * 08:31 -> 09:00
   * 16:00 + 4h -> lendemain 10:00
   */
  function calculateDueDate(createdAt: string | Moment, durationHours = DEFAULT_DURATION): string {
    const date = moment.isMoment(createdAt) ? createdAt.clone().local() : moment(createdAt).local()

    /**
     * Si la création est le week-end,
     * on commence directement lundi à 08:00.
     */
    if (date.isoWeekday() > 5) {
      while (date.isoWeekday() > 5) {
        date.add(1, 'day')
      }

      date.hour(WORK_START).minute(0).second(0).millisecond(0)
    }

    /**
     * Si création >= 18:00
     * -> prochain jour ouvré à 08:00
     */
    if (date.hour() >= WORK_END) {
      date.add(1, 'day')

      while (date.isoWeekday() > 5) {
        date.add(1, 'day')
      }

      date.hour(WORK_START).minute(0).second(0).millisecond(0)
    }

    /**
     * Si création avant 08:00
     * -> 08:00 le même jour.
     */
    if (date.hour() < WORK_START) {
      date.hour(WORK_START).minute(0).second(0).millisecond(0)
    } else if (date.minutes() > 30) {

    /**
     * Arrondi des minutes :
     *
     * 08:00 -> 08:00
     * 08:30 -> 08:00
     * 08:31 -> 09:00
     *
     * On ne fait cet arrondi que si la création
     * est bien dans la plage 08:00-17:59.
     */
      date.add(1, 'hour')
      date.minute(0)
      date.second(0)
      date.millisecond(0)

      /**
       * Exemple :
       * 17:31 -> 18:00
       *
       * Comme 18:00 est hors horaires,
       * on repart le lendemain à 08:00.
       */
      if (date.hour() >= WORK_END) {
        date.add(1, 'day')

        while (date.isoWeekday() > 5) {
          date.add(1, 'day')
        }

        date.hour(WORK_START).minute(0).second(0).millisecond(0)
      }
    } else {
      date.minute(0).second(0).millisecond(0)
    }

    let remaining = durationHours

    while (remaining > 0) {
      /**
       * Week-end
       */
      if (date.isoWeekday() > 5) {
        while (date.isoWeekday() > 5) {
          date.add(1, 'day')
        }

        date.hour(WORK_START).minute(0).second(0).millisecond(0)

        continue
      }

      /**
       * Avant 08:00
       */
      if (date.hour() < WORK_START) {
        date.hour(WORK_START).minute(0).second(0).millisecond(0)

        continue
      }

      /**
       * À partir de 18:00
       */
      if (date.hour() >= WORK_END) {
        date.add(1, 'day')

        while (date.isoWeekday() > 5) {
          date.add(1, 'day')
        }

        date.hour(WORK_START).minute(0).second(0).millisecond(0)

        continue
      }

      /**
       * Heures disponibles aujourd'hui
       */
      const availableToday = WORK_END - date.hour()

      /**
       * On peut terminer aujourd'hui
       */
      if (remaining <= availableToday) {
        date.add(remaining, 'hours')
        remaining = 0
      } else {
        /**
         * On consomme le reste de la journée
         * puis on reprend le prochain jour ouvré à 08:00.
         */
        remaining -= availableToday

        date.add(1, 'day')

        while (date.isoWeekday() > 5) {
          date.add(1, 'day')
        }

        date.hour(WORK_START).minute(0).second(0).millisecond(0)
      }
    }

    return date.format('YYYY-MM-DD HH:mm')
  }

  useEffect(() => {
    const statut = statutActions.find((st) => Number(st.value) === Number(formik.values.statut))

    if (statut?.label === 'Réalisée' && !formik.values.dateRealisation && !dateRealisation) {
      const now = moment.parseZone().format('YYYY-MM-DD HH:mm')

      setDateRealisation(now)

      formik.setFieldValue('dateRealisation', now, false)
    }
  }, [statutActions, formik.values.statut, formik.values.dateRealisation, dateRealisation])

  useEffect(() => {
    if (props?.data?.id && props?.data?.dateEcheance) {
      const existingDate = moment.parseZone(props.data.dateEcheance).format('YYYY-MM-DD HH:mm')
      setDateEcheance(existingDate)
      formik.setFieldValue('dateEcheance', existingDate, false)

      return
    }

    const createdAt = props?.data?.createdAt ? moment.parseZone(props.data.createdAt) : moment()
    const dueDate = calculateDueDate(createdAt, DEFAULT_DURATION)
    setDateEcheance(dueDate)
    formik.setFieldValue('dateEcheance', dueDate, false)
  }, [props?.data?.id, props?.data?.createdAt])

  const confirmRealisation = (action) => {
    closeDatePickers()
    setDatePickerBlocked(true)
    const activeElement = document.activeElement
    if (activeElement instanceof HTMLElement) activeElement.blur()

    setTimeout(() => {
      Swal.fire({
        title: 'Attention',
        text: 'Êtes-vous sûr de vouloir valider la réalisation de cette action ? Son statut passera en "Réalisé" et la demande retournera dans son flux de suivi classique.',
        icon: 'warning',
        confirmButtonText: 'Confirmer',
        cancelButtonText: 'Annuler',
        showCancelButton: true,
        confirmButtonColor: '#1BC5BD',
        cancelButtonColor: '#F64E60',
        target: document.body,
        allowOutsideClick: false,
        allowEscapeKey: true,
        didOpen: () => {
          const container = Swal.getContainer()

          setDatePickerBlocked(true)
          if (container) {
            container.style.zIndex = '20000'
          }
        },
        didClose() {
          setDatePickerBlocked(false)
        },
      }).then((alertResult) => {
        if (!alertResult.isConfirmed) {
          return
        }
        const idToast = toast.loading('Traitement en cours...')
        setLoading(true)

        finishActions(action)
          .then((res) => {
            setDatePickerBlocked(false)
            if (res?.status === 200 && res.data) {
              if (props?.onUpdateComment) props.onUpdateComment(res.data)
              toast.update(idToast, {
                render: 'Action réalisée',
                type: 'success',
                isLoading: false,
                autoClose: 2000,
              })

              if (props?.hideModal) {
                props.hideModal()
              }

              if (!props?.viaDemande) {
                navigate('/mes-actions/liste')
              }
            } else {
              toast.update(idToast, {
                render: 'Erreur lors de la réalisation',
                type: 'error',
                isLoading: false,
                autoClose: 2000,
              })
            }
          })
          .catch(() => {
            toast.update(idToast, {
              render: 'Erreur lors de la réalisation',
              type: 'error',
              isLoading: false,
              autoClose: 2000,
            })
          })
          .finally(() => {
            setLoading(false)
          })
      })
    }, 50)
  }

  const closeDatePickers = () => {
    document.querySelectorAll('.flatpickr-calendar.open').forEach((calendar) => {
      const instance = (
        calendar as HTMLElement & {
          _flatpickr?: {close: () => void}
        }
      )._flatpickr

      instance?.close()
    })

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
  }

  return (
    <div ref={formContainerRef}>
      <form onSubmit={formik.handleSubmit} noValidate className='form'>
        <div className='card-body border-top p-9'>
          <div className='row mb-6'>
            <div className='col-lg-6'>
              <label className='form-label fw-bold fs-6'>Date création</label>
              <input
                type='text'
                className='form-control form-control-lg'
                value={
                  props.data?.createdAt
                    ? moment(props.data.createdAt).format('DD MMMM YYYY HH:mm')
                    : moment().format('DD MMMM YYYY HH:mm')
                }
                disabled
              />
            </div>
            <div className='col-lg-6'>
              <label className='form-label fw-bold fs-6'>Date réalisation</label>
              <DateInput
                fieldName='dateRealisation'
                inputClassName='form-control'
                value={formik.values.dateRealisation || ''}
                setter={setDateRealisation}
                formik={formik}
                customHeight={true}
                enableTime={true}
                blockOpen={datePickerBlocked}
              />
            </div>
          </div>
          <div className='row mb-6'>
            <div className='col-lg-6'>
              <label className='form-label required fw-bold fs-6'>Titre</label>
              <TextInput
                fieldName='titre'
                inputType='text'
                inputClassName='form-control form-control-lg'
                formik={formik}
                disabled={props?.readOnly}
              />
            </div>
            <div className='col-lg-6'>
              <label className='form-label required fw-bold fs-6'>Type</label>
              {!loadingSelects.type && (
                <div className='mt-2'>
                  <span className='spinner-border spinner-border-sm align-middle'></span>
                </div>
              )}
              {loadingSelects.type && (
                <SelectInput
                  fieldName={'type'}
                  inputClassName={'form-select form-select-lg fw-bold'}
                  options={typeActions}
                  formik={formik}
                  disabled={props?.readOnly}
                />
              )}
            </div>
          </div>

          <div className='row mb-6'>
            <div className='col-lg-6'>
              <label className='form-label required fw-bold fs-6'>Statut</label>
              {!loadingSelects.statut && (
                <div className='mt-2'>
                  <span className='spinner-border spinner-border-sm align-middle' />
                </div>
              )}
              {loadingSelects.statut && (
                <SelectInput
                  fieldName={'statut'}
                  inputClassName={'form-select form-select-lg fw-bold'}
                  options={statutActions}
                  formik={formik}
                  disabled={!loadingSelects.statut || isCreate}
                />
              )}
            </div>
            <div className='col-lg-6'>
              <label className='form-label required fw-bold fs-6'>Origine</label>
              <SelectInput
                fieldName={'origine'}
                inputClassName={'form-select form-select-lg fw-bold'}
                options={origineMesActions}
                formik={formik}
                disabled={props?.readOnly || props?.viaDemande}
              />
            </div>
          </div>

          <div className='row mb-3'>
            <div className='col-lg-6'>
              <label className='form-label required fw-bold fs-6'>Date échéance</label>
              <DateInput
                fieldName='dateEcheance'
                inputClassName='form-control'
                value={formik.values.dateEcheance || ''}
                setter={setDateEcheance}
                formik={formik}
                customHeight={true}
                enableTime={true}
              />
            </div>

            <div className='col-lg-6'>
              <label className='form-label required fw-bold fs-6'>Assignée à</label>
              {!loadingSelects.userAssign && (
                <div className='mt-2'>
                  <span className='spinner-border spinner-border-sm align-middle'></span>
                </div>
              )}
              {loadingSelects.userAssign && (
                <SelectInput
                  fieldName={'userAssign'}
                  inputClassName={'form-select form-select-lg fw-bold'}
                  options={utilisateurs}
                  formik={formik}
                  disabled={!loadingSelects.userAssign || props?.readOnly || props?.viaDemande}
                />
              )}
            </div>
          </div>

          <div className='row mb-6'>
            <div className='col-lg-6'>
              <label className='form-label fw-bold fs-6'>Client</label>
              <SearchClient
                placeholder='Rechercher un client avec au moins 3 caractères'
                setClient={(client) => {
                  formik.setFieldValue('clientId', client?.id ?? null)
                }}
                initialClient={demandeSelected?.client ?? props?.data?.client}
                contactClient={demandeSelected?.contactedBy ?? props?.data?.demande?.contactedBy}
                disabled={props?.readOnly || props?.viaDemande}
              />
            </div>
            <div className='col-lg-6'>
              <label className='form-label fw-bold fs-6'>Demande</label>
              <SearchDemande
                placeholder='Rechercher une demande avec au moins 3 caractères'
                setDemande={(demande) => {
                  setDemandeSelected(demande)
                  formik.setFieldValue('demandeId', demande?.id ?? null)
                }}
                initialDemande={props?.data?.demande}
                disabled={props?.readOnly || props?.viaDemande}
              />
            </div>
          </div>

          <div className='row mb-6'>
            <div className='col-lg-12'>
              <label className='form-label fw-bold fs-6'>Commentaires création</label>
              <textarea
                name='commentaire'
                className='form-control form-control-flush'
                placeholder='Notes internes...'
                value={
                  props?.data?.type?.code === 'MESS CLIENT' &&
                  props?.data?.origine === 'Automatique'
                    ? formik?.values?.commentaire?.split('###').join('\n')
                    : formik.values.commentaire ?? ''
                }
                disabled={
                  (props?.data?.type?.code === 'MESS CLIENT' &&
                    props?.data?.origine === 'Automatique') ||
                  props?.readOnly
                }
                onChange={formik.handleChange}
                style={{
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontSize: '14px',
                  resize: 'none',
                  height: '80px',
                }}
              />
            </div>
          </div>

          {props.data.id && (
            <div className='row mb-6'>
              <div className='col-lg-12'>
                <label className='form-label fw-bold fs-6'>Commentaires résolution</label>
                <textarea
                  name='commentaireResolution'
                  className='form-control form-control-flush'
                  placeholder='Commentaires résolution...'
                  value={formik.values.commentaireResolution ?? ''}
                  disabled={props.data.statut.code === 'TERMINEE'}
                  onChange={formik.handleChange}
                  style={{
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    fontSize: '14px',
                    resize: 'none',
                    height: '80px',
                  }}
                />
              </div>
            </div>
          )}

          <div className='row mb-3'>
            <div className='col-lg-6 d-flex align-items-center'>
              <input
                className='form-check-input me-2'
                id='prioritaire'
                type='checkbox'
                value={1}
                name='prioritaire'
                checked={!!formik.values.prioritaire}
                onChange={(e) => {
                  formik.setFieldValue('prioritaire', e.target.checked)
                }}
                disabled={props?.readOnly}
              />
              <label htmlFor='prioritaire' className='form-label fw-bold fs-6 mb-0'>
                Marquer comme prioritaire
              </label>
            </div>
          </div>
        </div>
        {
          props?.data?.statut?.code !== 'TERMINEE'
          && ((!isCreate && shouldDisplayFonctionnalite(currentUser, 'TRAITER ACTION'))
          || (isCreate && shouldDisplayFonctionnalite(currentUser, 'AJOUTER ACTION')))
          && (
          <div className='card-footer d-flex justify-content-end py-6 px-9'>
            <button
              type='button'
              className='btn btn-light me-3'
              disabled={loading}
              onClick={() => {
                if (props?.clearValue) props.clearValue()
                formik.resetForm({
                  values: emptyValues,
                })

                if (!props?.viaDemande) {
                  navigate('/mes-actions/liste', {state: {view: props?.view, filters: props?.filters}})
                } else {
                  if (props?.cancelAction) props.cancelAction()
                }
                if (props.isRealisation && props?.hideModal) props.hideModal()
              }}
            >
              Annuler
            </button>
            <button
              type='submit'
              className='btn btn-primary'
              disabled={
                loading ||
                !selectsReady ||
                (location.pathname.includes('modifier') &&
                  typeActions?.filter((ta) => ta?.value === Number(formik.values?.type))?.[0]
                    ?.label === 'Message Client')
              }
            >
              {!loading && 'Enregistrer'}

              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Enregistrement...
                  <span className='spinner-border spinner-border-sm align-middle ms-2' />
                </span>
              )}
            </button>
          </div>
        )}
      </form>
    </div>
  )
}

export {MesActionsForm}
